import URL from 'url-parse';
import { isFQDN, isEmail, isMobilePhone } from 'validator';

import includes from 'lodash/includes';
import forEach from 'lodash/forEach';

const validSchemes = [
  'http:',
  'https:',
  'ftp:',
  'mailto:',
  'tel:'
];

const getParsedUrl = (url)=>
  URL(url, { baseUrl: '' });

export const getScheme = (url)=>
  getParsedUrl(url).protocol || null;

export const hasScheme = (url)=>
  !!getScheme(url);

export const hasValidScheme = (url)=>
  includes(validSchemes, getScheme(url));

export const isSchemeHttps = (url)=>
  getScheme(url) === 'https:';

export const isSchemeMailto = (url)=>
  getScheme(url) === 'mailto:';

export const isSchemeTel = (url)=>
  getScheme(url) === 'tel:';

export const isValidMailto = (url)=> {
  if(!isSchemeMailto(url)){
    return false;
  }

  const parsedUrl = getParsedUrl(url);
  const emailsString = parsedUrl.pathname;
  const emails = emailsString.split(/[;,]/);

  let isInvalid = false;
  forEach(emails, (email) => {
    if(!isEmail(email)){
      isInvalid = true;
    }
  });

  return !isInvalid;
};

export const isValidTel = (url)=> {
  if(!isSchemeTel(url)){
    return false;
  }

  const parsedUrl = getParsedUrl(url);

  return isMobilePhone(parsedUrl.pathname);
};

export const hasValidDomain = (url)=> {
  const { href, hostname, pathname } = getParsedUrl(url);

  if (isSchemeMailto(url)) {
    return isEmail(pathname);
  }

  if (isSchemeTel(url)) {
    return isMobilePhone(pathname);
  }

  return isFQDN(hasScheme(url) ? hostname : href);
};

export const hasAnyError = (url, { forceHttps = false, isSchemeRequired = false } = {})=> {
  return null !== getErrorMessage(url, { forceHttps, isSchemeRequired });
};

export const getErrorMessage = (url, { forceHttps = false, isSchemeRequired = false } = {})=> {
  const isMissingScheme = isSchemeRequired && !hasScheme(url);
  const isInvalidScheme = isSchemeRequired && !hasValidScheme(url);

  if(isSchemeMailto(url)){
    if(isValidMailto(url)){
      return null;

    } else {
      return 'Invalid URL. mailto links should have valid email addresses.';

    }
  }

  if(isSchemeTel(url)){
    if(isValidTel(url)){
      return null;

    } else {
      return 'Invalid URL. tel links should have valid phone numbers.';

    }
  }

  const isMissingHttps = forceHttps && !isSchemeHttps(url);
  const isMissingDomain = !hasValidDomain(url);

  return isMissingHttps ? 
      'Invalid URL. It must start with "https://".'
    : isMissingScheme? 
      'Invalid URL. It\'s missing the http:// (or https://) part.'
    : isInvalidScheme ? 
      `Invalid URL. "${getScheme(url)}" is not recognized as a valid start for a URL. Try "http://". `
    : isMissingDomain ? 
      'Invalid URL. Enter a valid domain (like "example.com").'
    : 
      null;
};

export const makeOnPasteHandler = (currentUrl, callback)=>
  ({ clipboardData })=>
    clipboardData.items[0].getAsString( val => {
      callback(
        currentUrl ? 
          val
        : hasScheme(val) ? 
          val 
        : 
          `http://${val}`
      );
    });

export const prependSchemeIfMissing = (url, defaultScheme='https') =>
  hasScheme(url) ? url : `${defaultScheme}://${url}`;

export const makeUrlHttps = (url) => {
  if (isSchemeHttps(url)) {
    return url;
  }

  const { href, hostname } = getParsedUrl(url);
  return `https://${hostname || href}`;
};
